/**
 * src/pages/admin/AdminUsers.js
 * 
 * Admin User Management page.
 * View all users, search, assign roles, ban/unban, view profiles.
 * Paginated list with real-time search.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Search,
  Shield,
  ShieldCheck,
  ShieldOff,
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Mail,
  Calendar,
  ExternalLink,
  UserCog,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import { useAdmin } from "../../hooks/useAdmin";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { errorDev } from '../../utils/logger.js';

const ROLES = [
  { id: "user", label: "User", Icon: Users, color: "text-gray-500" },
  { id: "admin", label: "Admin", Icon: Shield, color: "text-green-600 dark:text-green-400" },
  { id: "superadmin", label: "Super Admin", Icon: ShieldCheck, color: "text-purple-600 dark:text-purple-400" },
];

function formatDate(ts) {
  if (!ts) return "Unknown";
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return date.toLocaleDateString("en", { year: "numeric", month: "short", day: "numeric" });
}

// ─── User Row ───────────────────────────────────────────────────
function UserRow({ userData, onRoleChange, onBanToggle, currentAdminRole, adminUser }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedRole, setSelectedRole] = useState(userData.role || "user");
  const [banReason, setBanReason] = useState("");
  const [showBanForm, setShowBanForm] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    loading: false,
    action: null,
  });
  const toast = useToast();

  const isBanned = !!userData.banned;
  const isSuperAdmin = userData.role === "superadmin";

  const handleRoleChange = async () => {
    if (selectedRole === userData.role) return;
    if (isSuperAdmin && currentAdminRole !== "superadmin") {
      toast.warning("Only super admins can modify super admin roles");
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Confirm Role Change",
      message: `Change role to ${selectedRole}?`,
      loading: false,
      action: async () => {
        setUpdating(true);
        try {
          await adminService.withAdminRetry(() => adminService.updateUserRole(adminUser, userData.id, selectedRole));
          toast.success(`Role updated to ${selectedRole}`);
          onRoleChange?.();
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const handleBanToggle = async () => {
    if (isSuperAdmin) {
      toast.warning("Cannot ban super admin");
      return;
    }
    setConfirmDialog({
      open: true,
      title: isBanned ? "Unban User" : "Ban User",
      message: isBanned ? "Allow this user to use Orrith again?" : "This user will be blocked from normal access.",
      loading: false,
      action: async () => {
        setUpdating(true);
        try {
          await adminService.withAdminRetry(() => adminService.toggleUserBan(adminUser, userData.id, !isBanned, banReason));
          toast.success(isBanned ? "User unbanned" : "User banned");
          setShowBanForm(false);
          setBanReason("");
          onBanToggle?.();
        } finally {
          setUpdating(false);
        }
      },
    });
  };

  const runConfirmAction = async () => {
    if (!confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.action();
      setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(`Failed: ${err?.message || "Action failed"}`);
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl border ${isBanned ? 'border-red-300 dark:border-red-800' : 'border-gray-200 dark:border-neutral-700'} overflow-hidden`}>
      {/* Main Row */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-750 transition"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 text-sm font-bold text-green-700 dark:text-green-400">
            {userData.photoURL ? (
              <img src={userData.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              (userData.displayName || userData.email || "?").charAt(0).toUpperCase()
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900 dark:text-white truncate">
                {userData.displayName || userData.name || "No Name"}
              </span>
              {isBanned && (
                <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  BANNED
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userData.email}</p>
          </div>

          {/* Role Badge */}
          <span className={`
            px-2 py-0.5 rounded-full text-xs font-bold
            ${userData.role === 'superadmin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
              userData.role === 'admin' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400'}
          `}>
            {userData.role || "user"}
          </span>

          <div className="flex-shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </div>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-neutral-700 p-4 space-y-4">
          {/* User Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Mail className="w-4 h-4" />
              <span>{userData.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Joined {formatDate(userData.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <CheckCircle2 className={`w-4 h-4 ${userData.emailVerified ? 'text-green-500' : 'text-gray-400'}`} />
              <span>{userData.emailVerified ? "Email verified" : "Not verified"}</span>
            </div>
            <Link
              to={`/profile/${userData.id}`}
              className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              View Profile
            </Link>
          </div>

          {/* Role Management */}
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700">
            <div className="flex items-center gap-2 mb-2">
              <UserCog className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Role Management</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRole(r.id)}
                  disabled={r.id === "superadmin" && currentAdminRole !== "superadmin"}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium border transition
                    ${selectedRole === r.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500 disabled:opacity-40 disabled:cursor-not-allowed'
                    }
                  `}
                >
                  {r.label}
                </button>
              ))}
              {selectedRole !== userData.role && (
                <button
                  onClick={handleRoleChange}
                  disabled={updating}
                  className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition flex items-center gap-1 disabled:opacity-50"
                >
                  {updating ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <CheckCircle2 className="w-3 h-3" />}
                  Apply
                </button>
              )}
            </div>
          </div>

          {/* Ban/Unban */}
          <div className="flex flex-wrap gap-2">
            {!isBanned ? (
              <>
                {!showBanForm ? (
                  <button
                    onClick={() => setShowBanForm(true)}
                    disabled={isSuperAdmin}
                    className="px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition flex items-center gap-1 disabled:opacity-40"
                  >
                    <Ban className="w-3 h-3" />
                    Ban User
                  </button>
                ) : (
                  <div className="w-full space-y-2">
                    <input
                      type="text"
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Reason for banning (optional)…"
                      className="w-full px-3 py-2 border border-red-200 dark:border-red-800 rounded-lg bg-white dark:bg-neutral-900 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleBanToggle}
                        disabled={updating}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition flex items-center gap-1 disabled:opacity-50"
                      >
                        {updating ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <Ban className="w-3 h-3" />}
                        Confirm Ban
                      </button>
                      <button
                        onClick={() => { setShowBanForm(false); setBanReason(""); }}
                        className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400 text-xs font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full">
                {userData.banReason && (
                  <div className="mb-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-400 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>Ban reason: {userData.banReason}</span>
                  </div>
                )}
                <button
                  onClick={handleBanToggle}
                  disabled={updating}
                  className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium hover:bg-green-200 transition flex items-center gap-1 disabled:opacity-50"
                >
                  {updating ? <span className="orrith-loader orrith-loader-xs orrith-loader-primary" /> : <CheckCircle2 className="w-3 h-3" />}
                  Unban User
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        loading={confirmDialog.loading}
        destructive={true}
        confirmLabel="Confirm"
        onConfirm={runConfirmAction}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────
export default function AdminUsers() {
  const { role: currentAdminRole, user: adminUser } = useAdmin();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [profileStateFilter, setProfileStateFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadUsers = useCallback(async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const result = await adminService.listUsers(25, append ? lastDoc : null, {
        profileState: profileStateFilter,
      });
      setLoadError("");
      if (append) {
        setUsers((prev) => [...prev, ...result.users]);
      } else {
        setUsers(result.users);
      }
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      const msg = err?.message || "Failed to load users";
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [lastDoc, profileStateFilter]);

  useEffect(() => {
    loadUsers();
  }, [profileStateFilter]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await adminService.findUsers(searchQuery, 20);
        setSearchResults(results);
      } catch (err) {
        errorDev("Operation failed:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const displayedUsers = searchResults !== null ? searchResults : users;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              User Management
            </h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
              {users.length} loaded
            </span>
          </div>

          <div className="pb-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Profile state filter</label>
            <select
              value={profileStateFilter}
              onChange={(e) => {
                setLastDoc(null);
                setHasMore(true);
                setProfileStateFilter(e.target.value);
              }}
              className="w-full sm:w-64 px-3 py-2 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            >
              <option value="all">All users</option>
              <option value="complete">Completed profiles</option>
              <option value="incomplete">Incomplete or broken profiles</option>
            </select>
          </div>

          {/* Search */}
          <div className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email or name…"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm focus:ring-2 focus:ring-green-500 outline-none transition"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 orrith-loader orrith-loader-xs orrith-loader-primary" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-3">
        {loadError && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-xs text-red-700 dark:text-red-300 flex items-center justify-between gap-3">
            <span>{loadError}</span>
            <button
              onClick={() => loadUsers()}
              className="px-2.5 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
            >
              Retry
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="orrith-loader orrith-loader-md orrith-loader-primary" />
          </div>
        ) : displayedUsers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery ? "No users match your search" : "No users found"}
            </p>
          </div>
        ) : (
          <>
            {displayedUsers.map((u) => (
              <UserRow
                key={u.id}
                userData={u}
                currentAdminRole={currentAdminRole}
                adminUser={adminUser}
                onRoleChange={() => loadUsers()}
                onBanToggle={() => loadUsers()}
              />
            ))}

            {/* Load More */}
            {hasMore && searchResults === null && (
              <div className="text-center pt-4">
                <button
                  onClick={() => loadUsers(true)}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loadingMore && <span className="orrith-loader orrith-loader-xs orrith-loader-primary" />}
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
